import { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';
import useDebounce from '@/hooks/useDebounce';
import type { HarborResponse } from '../../types/harbor-management.types';
import type { RegionResponse } from '../../types/regions.types';
import type { IYachtData } from '../../types/yacht-management.types';
import type { OrganizationResponse } from '../../types/organization.types';
import { toAbsoluteUrl } from '@/utils';
import { DataGrid, KeenIcon, Modal } from '@/components';
import { IUsersData } from '.';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import { preventLeadingWhitespace } from '@/utils/helperFunctions';
import clsx from 'clsx';
import { phoneNumberValidation } from '@/utils/validations';
import { normalizeNationalPhoneNumber } from '@/utils/phone';
import { Statuses, statusOptions, UserTypes, Entity } from '@/constant';
import {
  useGetUsersQuery,
  useGetUserByIdQuery,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useRevokeUserTokensMutation,
} from '@/services/UserManagementService';
import { useGetHarborsQuery } from '@/services/HarborManagementService';
import { useGetRegionsQuery } from '@/services/RegionManagementService';
import { useGetYachtsQuery } from '@/services/YatchManagementService';
import { useGetOrganizationsQuery } from '@/services/OrganizationManagementService';
import {
  UserResponse,
  UserListQueryParams,
  UserIdResponse,
  BoaterAssigned,
  HarborAssigned,
} from '@/types/user-management.types';
import usePagination from '@/hooks/usePagination';
import { ErrorApiResponse, PaginatedApiResponse } from '@/types/api.types';
import { ConfirmationDialog } from '@/components/dialogBox';
import DateRangePicker from '@/components/inputControls/DateRangePicker';
import { usePermissions } from '@/hooks/usePermissions';
// import { LegalAcceptanceCard } from '@/components/legal-acceptance/LegalAcceptanceCard';
import {
  NationalitySelect,
  CountrySelect,
} from '@/components/CountryCodeSelect';
import CountryCodeSelect from '@/components/CountryCodeSelect/CountryCodeSelect';
import { userValidationSchema } from '@/utils/userValidations';
import moment from 'moment';
import { RolesService } from '@/services/roles/rolesService';
import { formatDisplayDate } from '@/utils/Date';
import { RootState } from '@/store/store';
import {
  setUserManagementDateRange,
  clearUserManagementDateRange,
} from '@/store/userManagementFiltersSlice';

interface UserValidationData {
  userName: string;
  userGmail: string;
  role?: string[];
  status: string;
  profilePicture: File | null;
  countryCode: string;
  countryShortCode: string;
  phoneCountryShortCode: string;
  phoneNumber: string;
  nationality: string;
  userCountryCode: string;
  region?: {
    id: string;
    name: string;
    status: number;
  };
  organisationName?: string;
}

interface ExtendedUsersData extends Omit<IUsersData, 'role'> {
  role: string[];
  user: {
    id: string;
    userName: string;
    userGmail: string;
    avatar: string;
    [key: string]: string;
  };
  harbor: HarborAssigned | null;
  boats: BoaterAssigned[] | null;
  organisationName?: string;
  organisationType?: string;
  contactEmail?: string;
  linkedRegions?: string[];
  publicHomepage?: string;
  description?: string;
  status: string;
  createdAt: string;
  profilePicture?: File | null;
  nationality?: string;
  userCountryCode?: string;
  countryCode?: string;
  countryShortCode?: string;
  phoneCountryShortCode?: string;
  phoneNumber?: string;
  email?: string;
  region?: {
    id: string;
    name: string;
    status: number;
  };
}

const Users = ({
  onStatusToggle,
}: {
  onStatusToggle?: (userId: string, currentStatus: string) => void;
}) => {
  const {
    totalPages,
    totalItems,
    handleTotalPages,
    handleTotalItems,
    debouncedValue,
    handleSearch,
    searchTerm,
    pagination,
    setPagination,
    resetSearch,
  } = usePagination();
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [roleFilter, setRoleFilter] = useState<string[]>([]);
  const [entityFilter, setEntityFilter] = useState<string>('all');
  const [specificEntityId, setSpecificEntityId] = useState<string>('all');
  const dispatch = useDispatch();
  const { startDate: tempStartDate, endDate: tempEndDate } = useSelector(
    (state: RootState) => state.userManagementFilters
  );
  const entitySearch = useDebounce(300);
  const { hasPermission } = usePermissions();
  const [roleOptions, setRoleOptions] = useState<
    Array<{ value: string; label: string }>
  >([]);

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    setPagination((prev) => ({
      ...prev,
      pageIndex: 0,
    }));
  };

  const handleRoleFilterChange = (value: string[]) => {
    setRoleFilter(value);
    setPagination((prev) => ({
      ...prev,
      pageIndex: 0,
    }));
  };

  const handleEntityFilterChange = (value: string) => {
    setEntityFilter(value);
    setSpecificEntityId('all');
    entitySearch.resetSearch();
    setPagination((prev) => ({
      ...prev,
      pageIndex: 0,
    }));
  };

  const handleSpecificEntityChange = (
    selectedOption: { value: string; label: string } | null
  ) => {
    setSpecificEntityId(selectedOption ? selectedOption.value : 'all');
    setPagination((prev) => ({
      ...prev,
      pageIndex: 0,
    }));
  };

  const handleDateRangeChange = (startDate: string, endDate: string) => {
    dispatch(setUserManagementDateRange({ startDate, endDate }));
    setPagination((prev) => ({
      ...prev,
      pageIndex: 0,
    }));
  };

  const resetFilters = () => {
    setStatusFilter('all');
    setRoleFilter([]);
    setEntityFilter('all');
    setSpecificEntityId('all');
    entitySearch.resetSearch();
    dispatch(clearUserManagementDateRange());
    if (searchTerm) {
      resetSearch();
    }
    setPagination((prev) => ({
      ...prev,
      pageIndex: 0,
    }));
  };

  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    userId: string | null;
    mode: 'view' | 'edit';
  }>({ isOpen: false, userId: null, mode: 'view' });
  const [confirmDialogState, setConfirmDialogState] = useState<{
    isOpen: boolean;
    userId: string | null;
    currentStatus: string | null;
  }>({ isOpen: false, userId: null, currentStatus: null });
  const [deleteActionModal, setDeleteActionModal] = useState<{
    isOpen: boolean;
    userId: string | null;
  }>({ isOpen: false, userId: null });
  const [revokeTokensModal, setRevokeTokensModal] = useState<{
    isOpen: boolean;
    userId: string | null;
  }>({ isOpen: false, userId: null });
  const [formData, setFormData] = useState<ExtendedUsersData | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const { enqueueSnackbar } = useSnackbar();
  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [updateUser, { isLoading: isUpdating }] = useUpdateUserMutation();
  const isRoleControlDisabled = modalState.mode === 'view' || isUpdating;
  const prevPhoneErrorRef = useRef(errors.phoneNumber);
  const [isRoleDropdownOpen, setIsRoleDropdownOpen] = useState(false);
  const roleDropdownRef = useRef<HTMLDivElement>(null);

  const { data: harborsData } = useGetHarborsQuery(
    { search: entitySearch.debouncedValue, limit: 100 },
    { skip: entityFilter !== String(Entity.HARBOR) }
  );

  const { data: regionsData } = useGetRegionsQuery(
    { search: entitySearch.debouncedValue, limit: 100 },
    { skip: entityFilter !== String(Entity.REGION) }
  );

  const { data: clubsData } = useGetYachtsQuery(
    { search: entitySearch.debouncedValue, limit: 100 },
    { skip: entityFilter !== String(Entity.CLUB) }
  );

  const { data: organizationsData } = useGetOrganizationsQuery(
    { search: entitySearch.debouncedValue, limit: 100 },
    { skip: entityFilter !== String(Entity.ORGANIZATION) }
  );

  const getEntityOptions = () => {
    const options = [{ value: 'all', label: `All ${getEntityLabel()}` }];

    if (entityFilter === String(Entity.HARBOR) && harborsData?.data) {
      harborsData.data.forEach((harbor: HarborResponse) => {
        if (harbor.id && harbor.name) {
          options.push({ value: harbor.id, label: harbor.name });
        }
      });
    } else if (entityFilter === String(Entity.REGION) && regionsData?.data) {
      regionsData.data.forEach((region: RegionResponse) => {
        if (region.id && region.name) {
          options.push({ value: region.id, label: region.name });
        }
      });
    } else if (entityFilter === String(Entity.CLUB) && clubsData?.data) {
      clubsData.data.forEach((club: IYachtData) => {
        if (club.id && club.name) {
          options.push({ value: club.id, label: club.name });
        }
      });
    } else if (
      entityFilter === String(Entity.ORGANIZATION) &&
      organizationsData?.data
    ) {
      organizationsData.data.forEach((org: OrganizationResponse) => {
        if (org.id && org.organisation_name) {
          options.push({ value: org.id, label: org.organisation_name });
        }
      });
    }

    return options;
  };

  const getEntityLabel = () => {
    switch (entityFilter) {
      case String(Entity.HARBOR):
        return 'Harbors';
      case String(Entity.REGION):
        return 'Regions';
      case String(Entity.CLUB):
        return 'Clubs';
      case String(Entity.ORGANIZATION):
        return 'Organizations';
      default:
        return 'Entities';
    }
  };

  useEffect(() => {
    const fetchRoleOptions = async () => {
      try {
        const options = await RolesService.getRoleOptions();
        setRoleOptions(options);
      } catch (error) {
        console.error('Failed to fetch role options:', error);
        setRoleOptions([]);
      }
    };

    fetchRoleOptions();
  }, []);

  const [deleteUser] = useDeleteUserMutation();
  const [revokeUserTokens, { isLoading: isRevokingTokens }] =
    useRevokeUserTokensMutation();

  const statusMap = useMemo((): { [key: number]: string } => {
    return {
      [Statuses.ACTIVE]: 'Active',
      [Statuses.INACTIVE]: 'Inactive',
      [Statuses.DELETED]: 'Inactive',
      [Statuses.BLOCKED]: 'Inactive',
    };
  }, []);

  const roleLabelMap = useMemo(() => {
    return new Map(roleOptions.map((opt) => [opt.value, opt.label]));
  }, [roleOptions]);

  const formatRoleLabel = useCallback(
    (roleKey: string) => {
      const fromOptions = roleLabelMap.get(roleKey);
      if (fromOptions) return fromOptions;

      return roleKey
        .split('_')
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
    },
    [roleLabelMap]
  );

  const queryParams: UserListQueryParams = useMemo(
    () => ({
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
      status:
        statusFilter !== 'all'
          ? statusFilter === 'Active'
            ? Statuses.ACTIVE
            : Statuses.INACTIVE
          : undefined,
      roles: roleFilter.length > 0 ? roleFilter : undefined,
      search: debouncedValue,
      start_date:
        tempStartDate && moment(tempStartDate, 'DD/MM/YYYY', true).isValid()
          ? moment(tempStartDate, 'DD/MM/YYYY').format('YYYY-MM-DD')
          : undefined,
      end_date: (() => {
        const hasStart =
          tempStartDate && moment(tempStartDate, 'DD/MM/YYYY', true).isValid();
        const hasEnd =
          tempEndDate && moment(tempEndDate, 'DD/MM/YYYY', true).isValid();
        if (hasEnd) {
          return moment(tempEndDate, 'DD/MM/YYYY').format('YYYY-MM-DD');
        }
        if (hasStart) {
          return moment(tempStartDate, 'DD/MM/YYYY').format('YYYY-MM-DD');
        }
        return undefined;
      })(),
      entity: entityFilter !== 'all' ? parseInt(entityFilter) : undefined,
      entityId: specificEntityId !== 'all' ? specificEntityId : undefined,
    }),
    [
      pagination.pageIndex,
      pagination.pageSize,
      statusFilter,
      roleFilter,
      debouncedValue,
      tempStartDate,
      tempEndDate,
      entityFilter,
      specificEntityId,
    ]
  );

  const {
    data: usersResponse,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useGetUsersQuery(queryParams) as {
    data: PaginatedApiResponse<UserResponse[]> | undefined;
    isLoading: boolean;
    isFetching: boolean;
    isError: boolean;
    refetch: () => void;
  };

  const { data: userDetails, isLoading: isUserLoading } = useGetUserByIdQuery(
    modalState.userId || '',
    {
      skip: !modalState.isOpen || !modalState.userId,
    }
  );

  const validatePhoneNumber = useCallback(
    async (phone: string, shortCode: string) => {
      try {
        const schema = Yup.object().shape({
          phoneNumber: phoneNumberValidation(
            'Phone number',
            false,
            'countryShortCode'
          ),
        });

        const tempFormData = {
          phoneNumber: phone,
          countryShortCode: shortCode,
        };

        await schema.validateAt('phoneNumber', tempFormData, {
          abortEarly: true,
        });
        if (prevPhoneErrorRef.current !== '') {
          setErrors((prev) => {
            if (prev.phoneNumber === '') return prev;
            return { ...prev, phoneNumber: '' };
          });
          prevPhoneErrorRef.current = '';
        }
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const newError = err.errors[0] || 'Invalid phone number';
          if (prevPhoneErrorRef.current !== newError) {
            setErrors((prev) => {
              if (prev.phoneNumber === newError) return prev;
              return { ...prev, phoneNumber: newError };
            });
            prevPhoneErrorRef.current = newError;
          }
        }
      }
    },
    [setErrors]
  );

  useEffect(() => {
    if (formData?.countryShortCode && modalState.isOpen) {
      validatePhoneNumber(
        formData.phoneNumber || '',
        formData.countryShortCode
      );
    } else if (
      !formData?.phoneNumber &&
      errors.phoneNumber &&
      modalState.isOpen
    ) {
      setErrors((prev) => {
        if (!prev.phoneNumber) return prev;
        return { ...prev, phoneNumber: '' };
      });
      prevPhoneErrorRef.current = '';
    }
  }, [
    formData?.phoneNumber,
    formData?.countryShortCode,
    validatePhoneNumber,
    errors.phoneNumber,
  ]);

  useEffect(() => {
    if (userDetails && modalState.isOpen) {
      const user = userDetails as UserIdResponse;

      const roles = user.roles;

      const normalizedUser: ExtendedUsersData = {
        user: {
          id: user.id,
          avatar:
            user.profile_picture ||
            user.profile_path ||
            '/media/avatars/defult_avtar.png',
          userName: user.name,
          userGmail: user.email,
        },
        role: roles,
        status: statusMap[user.status] || statusOptions[0].value,
        createdAt: user.created_at,
        countryCode: user.country_code || '+1',
        countryShortCode: user.country_short_code || 'US',
        phoneCountryShortCode:
          user.phone_country_short_code || user.country_short_code || 'US',
        userCountryCode:
          user.user_country_code || user.country_short_code || '',
        phoneNumber: user.mobile
          ? normalizeNationalPhoneNumber(user.mobile, user.country_short_code)
          : '',
        nationality: user.nationality || '',
        harbor: roles.includes('marina')
          ? {
              harbor_id: user?.harbor?.harbor_id ?? '',
              harbor_name: user?.harbor?.harbor_name ?? '',
              harbor_location: user?.harbor?.harbor_location ?? '',
            }
          : null,
        boats: roles.includes('boater') ? user?.boats : null,
        profilePicture: null,
      };
      setFormData(normalizedUser);
      const existingProfilePicture = user.profile_picture || user.profile_path;
      if (existingProfilePicture) {
        setPreviewUrl(
          existingProfilePicture.startsWith('http')
            ? existingProfilePicture
            : toAbsoluteUrl(existingProfilePicture)
        );
      } else {
        setPreviewUrl(null);
      }
      setErrors({});
      if (modalState.mode === 'edit') {
        setTimeout(() => inputRef.current?.focus(), 100);
      }
    }
  }, [userDetails, modalState.isOpen, modalState.mode]);

  useEffect(() => {
    refetch();
  }, [
    statusFilter,
    roleFilter,
    entityFilter,
    specificEntityId,
    tempStartDate,
    tempEndDate,
    debouncedValue,
    pagination.pageIndex,
    pagination.pageSize,
    refetch,
  ]);

  useEffect(() => {
    if (usersResponse) {
      handleTotalPages(usersResponse.totalPages || 1);
      handleTotalItems(usersResponse.totalItems || 0);
    }
  }, [usersResponse, handleTotalPages, handleTotalItems]);

  const filteredData: ExtendedUsersData[] = useMemo(() => {
    if (!usersResponse?.data) return [];

    return usersResponse.data.map(
      (user: UserResponse): ExtendedUsersData => ({
        user: {
          id: user.id,
          avatar: user.profile_path || '/media/avatars/defult_avtar.png',
          userName: user.name,
          userGmail: user.email,
        },
        role: user.roles
          ? user.roles.map((r) => formatRoleLabel(r))
          : ['Unassigned Role'],
        status: statusMap[user.status] || statusOptions[0].value,
        createdAt: user.created_at,
        countryCode: user.country_code || '+1',
        phoneNumber: user.mobile || '',
        nationality: user.nationality || 'US',
        harbor:
          user.user_type === UserTypes.MARINA
            ? {
                harbor_id: user?.harbor?.harbor_id ?? '',
                harbor_name: user?.harbor?.harbor_name ?? '',
                harbor_location: user?.harbor?.harbor_location ?? '',
              }
            : null,
        boats: user.user_type === UserTypes.BOATER ? user?.boats : null,
      })
    );
  }, [formatRoleLabel, statusMap, usersResponse]);

  const handleStatusToggle = (userId: string, currentStatus: string) => {
    if (onStatusToggle) {
      onStatusToggle(userId, currentStatus);
    } else {
      setConfirmDialogState({ isOpen: true, userId, currentStatus });
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteActionModal.userId) return;
    try {
      await deleteUser(deleteActionModal.userId).unwrap();
      if (typeof refetch === 'function') {
        refetch();
      }
      setDeleteActionModal({
        isOpen: false,
        userId: null,
      });
    } catch (error) {
      console.error('Failed to delete user:', error);
    }
  };

  const handleRevokeUserTokens = async () => {
    if (!revokeTokensModal.userId) return;
    try {
      await revokeUserTokens(revokeTokensModal.userId).unwrap();
      enqueueSnackbar('All access tokens revoked for this user.', {
        variant: 'success',
      });
      setRevokeTokensModal({ isOpen: false, userId: null });
    } catch (error) {
      console.error('Failed to revoke user tokens:', error);
      enqueueSnackbar('Failed to revoke access tokens.', { variant: 'error' });
    }
  };

  const handleConfirmStatusToggle = async () => {
    if (!confirmDialogState.userId || !confirmDialogState.currentStatus) return;

    try {
      const newStatus =
        confirmDialogState.currentStatus === 'Active'
          ? Statuses.INACTIVE
          : Statuses.ACTIVE;

      const formData = new FormData();
      formData.append('status', String(newStatus));

      await updateUser({
        user_id: confirmDialogState.userId,
        data: formData,
      }).unwrap();

      enqueueSnackbar(`User status updated to ${statusMap[newStatus]}`, {
        variant: 'success',
      });

      setConfirmDialogState({
        isOpen: false,
        userId: null,
        currentStatus: null,
      });
    } catch (error) {
      console.error('Failed to update user status:', error);
      enqueueSnackbar('Failed to update user status', {
        variant: 'error',
      });
    }
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialogState({ isOpen: false, userId: null, currentStatus: null });
  };

  const columns = useMemo(
    () => [
      {
        accessor: (row: ExtendedUsersData) => row.user.userName,
        id: 'users',
        header: 'Name',
        enableSorting: false,
        cell: ({ row }: { row: { original: ExtendedUsersData } }) => {
          return (
            <div className="flex items-center gap-4">
              <img
                src={toAbsoluteUrl(`${row.original.user.avatar}`)}
                className="rounded-full size-9 shrink-0"
                alt=""
              />
              <div className="flex flex-col gap-0.5">
                <button
                  onClick={() => handleOpenModal(row.original.user.id, 'view')}
                  className="text-sm font-medium text-gray-900 hover:text-primary-active contrast:hover:text-blue-300 mb-px"
                  aria-label={`View ${row.original.user.userName}`}
                >
                  {row.original.user.userName || '-'}
                </button>
              </div>
            </div>
          );
        },
        meta: {
          className: 'min-w-[250px]',
          cellClassName: 'text-gray-800 font-normal',
        },
      },
      {
        accessor: (row: ExtendedUsersData) => row.user.userGmail,
        id: 'email',
        header: 'Email',
        enableSorting: false,
        cell: ({ row }: { row: { original: ExtendedUsersData } }) => {
          return (
            <div className="flex items-center text-gray-800 font-normal gap-1.5">
              {row.original.user.userGmail}
            </div>
          );
        },
        meta: {
          className: 'min-w-[180px]',
        },
      },
      {
        accessor: (row: ExtendedUsersData) => row.role,
        id: 'role',
        header: 'Role',
        enableSorting: false,
        cell: ({ row }: { row: { original: ExtendedUsersData } }) => {
          return row.original.role.length > 0
            ? row.original.role.join(', ')
            : '-';
        },
        meta: {
          className: 'min-w-[180px]',
          cellClassName: 'text-gray-800 font-normal',
        },
      },
      {
        accessor: (row: ExtendedUsersData) => row.status,
        id: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }: { row: { original: ExtendedUsersData } }) => {
          return (
            <button
              onClick={() =>
                handleStatusToggle(row.original.user.id, row.original.status)
              }
              className={clsx(
                'font-normal cursor-pointer px-2 py-1 rounded',
                row.original.status === 'Active'
                  ? 'bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-200 contrast:bg-green-950 contrast:text-green-200'
                  : 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200 contrast:bg-red-950 contrast:text-red-200'
              )}
              role="button"
              tabIndex={0}
              onKeyDown={(e) =>
                e.key === 'Enter' &&
                handleStatusToggle(row.original.user.id, row.original.status)
              }
              aria-label={`${row.original.status}, toggle status for ${row.original.user.userName} to ${row.original.status === 'Active' ? 'Inactive' : 'Active'}`}
            >
              {row.original.status}
            </button>
          );
        },
        meta: {
          className: 'min-w-[100px]',
          cellClassName: 'text-gray-800 font-normal',
        },
      },
      {
        accessor: (row: ExtendedUsersData) => row.createdAt,
        id: 'created-at',
        header: 'Created At',
        enableSorting: false,
        cell: ({ row }: { row: { original: ExtendedUsersData } }) => {
          return formatDisplayDate(row.original.createdAt);
        },
        meta: {
          className: 'min-w-[150px]',
          cellClassName: 'text-gray-700 font-normal contrast:text-white/50',
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }: { row: { original: ExtendedUsersData } }) => {
          return (
            <div className="flex items-center gap-2">
              {hasPermission('user_management_read') && (
                <button
                  type="button"
                  onClick={() => handleOpenModal(row.original.user.id, 'view')}
                  className="btn btn-sm btn-icon btn-light btn-outline"
                  title="View"
                  aria-label={`View ${row.original.user.userName}`}
                >
                  <KeenIcon
                    type="filled"
                    icon="eye"
                    className="text-gray-500"
                  />
                </button>
              )}
              {hasPermission('user_management_update') && (
                <button
                  type="button"
                  onClick={() => handleOpenModal(row.original.user.id, 'edit')}
                  className="btn btn-sm btn-icon btn-light btn-outline"
                  title="Edit"
                  aria-label={`Edit ${row.original.user.userName}`}
                >
                  <KeenIcon
                    type="filled"
                    icon="pencil"
                    className="text-gray-500"
                  />
                </button>
              )}
              {hasPermission('user_management_update') && (
                <button
                  type="button"
                  onClick={() =>
                    setRevokeTokensModal({
                      isOpen: true,
                      userId: row.original.user.id,
                    })
                  }
                  className="btn btn-sm btn-icon btn-light btn-outline focus:outline-none focus:ring-2 focus:ring-amber-500"
                  title="Revoke all sessions"
                  aria-label={`Revoke all sessions for ${row.original.user.userName}`}
                  disabled={isUpdating || isRevokingTokens}
                >
                  <KeenIcon
                    type="filled"
                    icon="shield-slash"
                    className="text-amber-600"
                  />
                </button>
              )}
              {hasPermission('user_management_delete') && (
                <button
                  type="button"
                  onClick={() =>
                    setDeleteActionModal({
                      isOpen: true,
                      userId: row.original.user.id,
                    })
                  }
                  className="btn btn-sm btn-icon btn-light btn-outline focus:outline-none focus:ring-2 focus:ring-red-500"
                  title="Delete"
                  aria-label={`Delete ${row.original.user.userName}'s account`}
                  disabled={isUpdating}
                >
                  <KeenIcon
                    type="filled"
                    icon="trash"
                    className="text-red-500"
                  />
                </button>
              )}
            </div>
          );
        },
        meta: {
          className: 'w-[120px]',
        },
      },
    ],
    []
  );

  const handleOpenModal = (userId: string, mode: 'view' | 'edit') => {
    setModalState({ isOpen: true, userId, mode });
  };

  const handleCloseModal = () => {
    setModalState({ isOpen: false, userId: null, mode: 'view' });
    setFormData(null);
    setPreviewUrl(null);
    setErrors({});
  };

  const handleRoleChange = useCallback((role: string) => {
    setFormData((prev) => {
      if (!prev) return null;

      if (role === 'boater' || role === 'marina') {
        let newRoles = [...prev.role];

        if (newRoles.includes(role)) {
          newRoles = newRoles.filter((r) => r !== role);
        } else {
          if (role === 'boater') {
            newRoles = newRoles.filter((r) => r !== 'marina');
          } else if (role === 'marina') {
            newRoles = newRoles.filter((r) => r !== 'boater');
          }
          newRoles.push(role);
        }

        return {
          ...prev,
          role: newRoles,
        };
      } else {
        let newRoles = [...prev.role];

        newRoles = newRoles.includes(role)
          ? newRoles.filter((r) => r !== role)
          : [...newRoles, role];

        return {
          ...prev,
          role: newRoles,
        };
      }
    });
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          profilePicture: file,
        };
      });
      setPreviewUrl(URL.createObjectURL(file));
      setErrors((prev) => ({ ...prev, profilePicture: '' }));
    }
  };

  const handleRemoveImage = () => {
    setFormData((prev) => {
      if (!prev) return null;
      return {
        ...prev,
        profilePicture: null,
      };
    });
    setPreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleInputChange = useCallback(
    (
      e: React.ChangeEvent<
        HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
      >
    ) => {
      const { name, value } = e.target;

      setFormData((prev) => {
        if (!prev) return null;

        if (name === 'userName' || name === 'userGmail') {
          const userKey = name as keyof typeof prev.user;

          if (prev.user[userKey] === value) return prev;

          return {
            ...prev,
            user: { ...prev.user, [userKey]: value },
          };
        }

        const topLevelKey = name as keyof ExtendedUsersData;

        if (prev[topLevelKey] === value) return prev;

        return {
          ...prev,
          [topLevelKey]: value,
        };
      });

      setErrors((prev) => ({ ...prev, [name]: '' }));
    },
    []
  );

  const handleUserCountryChange = useCallback(
    (_code: string, shortCode: string) => {
      setFormData((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          userCountryCode: shortCode,
          countryShortCode: shortCode,
        };
      });

      setErrors((prev) => ({
        ...prev,
        countryShortCode: '',
      }));
    },
    []
  );

  const handleCountryCodeChange = useCallback(
    (value: string, shortCode: string) => {
      setFormData((prev) => {
        if (!prev) return null;
        if (
          prev.countryCode === value &&
          prev.phoneCountryShortCode === shortCode &&
          prev.countryShortCode === shortCode
        ) {
          return prev;
        }
        return {
          ...prev,
          countryCode: value,
          phoneCountryShortCode: shortCode,
          countryShortCode: shortCode,
        };
      });
      setErrors((prev) => ({
        ...prev,
        countryCode: '',
        phoneCountryShortCode: '',
        countryShortCode: '',
      }));
    },
    []
  );

  const handleNationalityChange = useCallback((value: string) => {
    setFormData((prev) => {
      if (!prev) return null;
      if (prev.nationality === value) return prev;
      return {
        ...prev,
        nationality: value,
      };
    });
    setErrors((prev) => ({ ...prev, nationality: '' }));
  }, []);

  const handleBlur = async (field: string) => {
    if (!formData) return;
    try {
      await userValidationSchema.validateAt(field, {
        userName: formData.user.userName,
        userGmail: formData.user.userGmail,
        role: formData.role,
        status: formData.status,
        profilePicture: formData.profilePicture ?? null,
        countryCode: formData.countryCode,
        countryShortCode: formData.countryShortCode || '',
        phoneCountryShortCode:
          formData.phoneCountryShortCode || formData.countryShortCode || '',
        phoneNumber: formData.phoneNumber,
        nationality: formData.nationality,
        userCountryCode: formData.userCountryCode || '',
      });
      setErrors((prev) => ({ ...prev, [field]: '' }));
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        setErrors((prev) => ({ ...prev, [field]: err.message }));
      }
    }
  };

  const handleSave = async () => {
    if (!formData || !modalState.userId) return;

    try {
      const isMultiRole = formData.role.length > 1;
      const validationData: UserValidationData = {
        userName: formData.user.userName,
        userGmail: formData.user.userGmail,
        role: formData.role,
        status: formData.status,
        profilePicture: formData.profilePicture ?? null,
        countryCode: formData.countryCode || '',
        countryShortCode: formData.countryShortCode || '',
        phoneCountryShortCode:
          formData.phoneCountryShortCode || formData.countryShortCode || 'US',
        phoneNumber: formData.phoneNumber || '',
        nationality: formData.nationality || '',
        userCountryCode: formData.userCountryCode || '',
        region: formData.region,
        organisationName: formData.organisationName,
      };
      if (!isMultiRole) {
        if (formData.region) validationData.region = formData.region;
        if (formData.organisationName)
          validationData.organisationName = formData.organisationName;
      }

      await userValidationSchema.validate(validationData, {
        abortEarly: false,
      });

      const originalUser = userDetails as UserIdResponse;
      if (!originalUser) return;

      const payload = new FormData();

      const newRoles = formData.role;
      const originalRoles = originalUser.roles || [];
      if (
        JSON.stringify([...newRoles].sort()) !==
        JSON.stringify([...originalRoles].sort())
      ) {
        if (newRoles.length === 0) {
          payload.append('roles[]', '');
        } else {
          newRoles.forEach((role) => {
            payload.append('roles[]', role);
          });
        }
      }

      if (formData.user.userName !== originalUser.name) {
        payload.append('name', formData.user.userName);
      }
      if (formData.status !== statusMap[originalUser.status]) {
        const statusKey = Object.keys(statusMap).find(
          (key) => statusMap[parseInt(key)] === formData.status
        );
        if (statusKey) payload.append('status', statusKey);
      }
      if (formData.profilePicture) {
        payload.append('profile_picture', formData.profilePicture);
      } else if (originalUser.profile_picture && !formData.profilePicture) {
        payload.append('remove_profile_picture', 'true');
      }
      if (formData.countryCode !== originalUser.country_code) {
        payload.append('country_code', formData.countryCode as string);
      }
      if (formData.countryShortCode !== originalUser.country_short_code) {
        payload.append(
          'country_short_code',
          formData.countryShortCode as string
        );
      }
      const origMobile = originalUser?.mobile
        ? normalizeNationalPhoneNumber(
            originalUser.mobile,
            originalUser.country_short_code
          )
        : '';
      const newMobile = formData.phoneNumber
        ? normalizeNationalPhoneNumber(
            formData.phoneNumber as string,
            formData.countryShortCode as string
          )
        : '';

      if (newMobile !== origMobile) {
        payload.append('mobile', newMobile);
      }
      if (formData.nationality !== originalUser.nationality) {
        payload.append('nationality', formData.nationality as string);
      }
      if (!isMultiRole) {
        if (formData.region && formData.region !== originalUser.region) {
          payload.append('region', JSON.stringify(formData.region));
        }
      }

      if (Array.from(payload.keys()).length > 0) {
        await updateUser({
          user_id: modalState.userId,
          data: payload,
        }).unwrap();

        enqueueSnackbar('User updated successfully', { variant: 'success' });
        refetch();
      } else {
        enqueueSnackbar('No changes detected');
      }

      setErrors({});
      handleCloseModal();
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const validationErrors: { [key: string]: string } = {};
        err.inner.forEach((error: Yup.ValidationError) => {
          if (error.path) {
            validationErrors[error.path] = error.message;
          }
        });
        setErrors(validationErrors);
        enqueueSnackbar('Please fix the form errors', { variant: 'error' });
      } else {
        const errorMessage = (err as ErrorApiResponse).data?.message;
        enqueueSnackbar(errorMessage, { variant: 'error' });
      }
    }
  };

  return (
    <div className="card card-grid min-w-full">
      <div className="card-header flex flex-wrap items-center gap-2 sm:gap-4 lg:gap-6 justify-between">
        <div className="flex flex-wrap items-center gap-2 sm:gap-4">
          {/* Search Input */}
          <div
            className="flex-shrink-0"
            role="search"
            aria-label="Search users"
          >
            <label
              className="input input-sm h-8 sm:h-10 flex items-center"
              htmlFor="search-users"
              aria-label="Search users"
            >
              <KeenIcon
                type="filled"
                icon="magnifier"
                className="text-gray-500"
              />
              <input
                id="search-users"
                placeholder="Search users"
                type="text"
                value={searchTerm}
                onChange={handleSearch}
                className="w-full sm:w-48 h-8 sm:h-10 text-sm input border-0 scale-y-[0.9] rounded-none"
                aria-label="Search users"
              />
            </label>
          </div>

          {/* Status Filter */}
          <div className="flex-shrink-0">
            <label htmlFor="filter-status" className="sr-only">
              Filter by status
            </label>
            <select
              id="filter-status"
              className="select select-sm w-28 h-8 sm:h-10 text-sm"
              value={statusFilter}
              onChange={(e) => handleStatusFilterChange(e.target.value)}
              aria-label="Filter by status"
            >
              <option value="all">All Status</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>

          {/* Role Filter */}
          <div className="flex-shrink-0">
            <label htmlFor="filter-role" className="sr-only">
              Filter by role
            </label>
            <select
              id="filter-role"
              className="select select-sm w-44 h-8 sm:h-10 text-sm"
              value={roleFilter[0] || ''}
              onChange={(e) => {
                const value = e.target.value;
                handleRoleFilterChange(value ? [value] : []);
              }}
              aria-label="Filter by role"
            >
              <option value="">All Roles</option>
              {roleOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Entity Filter */}
          <div className="flex-shrink-0">
            <label htmlFor="filter-entity" className="sr-only">
              Filter by entity
            </label>
            <select
              id="filter-entity"
              className="select select-sm w-32 h-8 sm:h-10 text-sm"
              value={entityFilter}
              onChange={(e) => handleEntityFilterChange(e.target.value)}
              aria-label="Filter by entity"
            >
              <option value="all">All Entities</option>
              <option value={Entity.HARBOR}>Harbor</option>
              <option value={Entity.CLUB}>Club</option>
              <option value={Entity.REGION}>Region</option>
              <option value={Entity.ORGANIZATION}>Organization</option>
            </select>
          </div>

          {/* Specific Entity Filter - Conditional */}
          {entityFilter !== 'all' && (
            <div
              className="flex-shrink-0 w-48"
              role="search"
              aria-label={`Search ${getEntityLabel().toLowerCase()}`}
            >
              <label htmlFor="search-specific-entity" className="sr-only">
                {`Search ${getEntityLabel().toLowerCase()}`}
              </label>
              <Select
                options={getEntityOptions()}
                value={
                  getEntityOptions().find(
                    (option) => option.value === specificEntityId
                  ) || null
                }
                onChange={handleSpecificEntityChange}
                onInputChange={(inputValue) =>
                  entitySearch.handleSearch(null, inputValue)
                }
                inputValue={entitySearch.searchTerm}
                placeholder={`Search ${getEntityLabel().toLowerCase()}...`}
                isSearchable={true}
                isClearable={false}
                isMulti={false}
                inputId="search-specific-entity"
                aria-label={`Search ${getEntityLabel().toLowerCase()}`}
                className="text-sm"
                classNamePrefix="react-select"
                noOptionsMessage={() =>
                  `No ${getEntityLabel().toLowerCase()} found`
                }
                styles={{
                  control: (provided, state) => ({
                    ...provided,
                    minHeight: '32px',
                    height: '32px',
                    fontSize: '14px',
                    borderColor: state.isFocused ? '#3B82F6' : '#D1D5DB',
                    boxShadow: state.isFocused ? '0 0 0 1px #3B82F6' : 'none',
                    '&:hover': {
                      borderColor: '#9CA3AF',
                    },
                    '@media (min-width: 640px)': {
                      minHeight: '40px',
                      height: '40px',
                    },
                  }),
                  valueContainer: (provided) => ({
                    ...provided,
                    height: '30px',
                    padding: '0 6px',
                    '@media (min-width: 640px)': {
                      height: '38px',
                      padding: '0 8px',
                    },
                  }),
                  input: (provided) => ({
                    ...provided,
                    margin: '0px',
                    paddingTop: '0px',
                    paddingBottom: '0px',
                  }),
                  indicatorSeparator: () => ({
                    display: 'none',
                  }),
                  indicatorsContainer: (provided) => ({
                    ...provided,
                    height: '30px',
                    '@media (min-width: 640px)': {
                      height: '38px',
                    },
                  }),
                  menu: (provided) => ({
                    ...provided,
                    zIndex: 50,
                    fontSize: '14px',
                    backgroundColor:
                      document.documentElement.classList.contains('contrast') ||
                      document.documentElement.classList.contains('dark')
                        ? '#0b0c10'
                        : 'white',
                    border:
                      document.documentElement.classList.contains('contrast') ||
                      document.documentElement.classList.contains('dark')
                        ? '1px solid #1b1c22'
                        : '1px solid #D1D5DB',
                    borderRadius: '0.5rem',
                    boxShadow: '0px 7px 18px 0px rgba(0, 0, 0, 0.09)',
                  }),
                  option: (provided, state) => ({
                    ...provided,
                    fontSize: '14px',
                    backgroundColor:
                      document.documentElement.classList.contains('contrast') ||
                      document.documentElement.classList.contains('dark')
                        ? '#0b0c10'
                        : state.isSelected
                          ? '#3B82F6'
                          : state.isFocused
                            ? '#F3F4F6'
                            : 'white',
                    color:
                      document.documentElement.classList.contains('contrast') ||
                      document.documentElement.classList.contains('dark')
                        ? state.isSelected
                          ? '#ffffff'
                          : '#b5b7c8'
                        : state.isSelected
                          ? 'white'
                          : '#374151',
                    padding: '8px 12px',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor:
                        document.documentElement.classList.contains(
                          'contrast'
                        ) || document.documentElement.classList.contains('dark')
                          ? state.isSelected
                            ? '#172331'
                            : '#333333'
                          : state.isSelected
                            ? '#3B82F6'
                            : '#F3F4F6',
                    },
                  }),
                }}
              />
            </div>
          )}

          {/* Date Range Picker */}
          <div className="flex-shrink-0">
            <DateRangePicker
              startDateName="startDate"
              endDateName="endDate"
              isMandatory={false}
              value={[tempStartDate, tempEndDate]}
              onChange={([newStartDate, newEndDate]) => {
                handleDateRangeChange(
                  newStartDate as string,
                  newEndDate as string
                );
              }}
              allowPastDates={true}
              allowFutureDates={false}
              className="w-full sm:w-64 h-8 sm:h-10 text-sm"
            />
          </div>
        </div>

        {/* Clear Filters Button */}
        {(statusFilter !== 'all' ||
          roleFilter.length > 0 ||
          entityFilter !== 'all' ||
          specificEntityId !== 'all' ||
          tempStartDate !== '' ||
          tempEndDate !== '' ||
          searchTerm !== '') && (
          <div className="flex items-center">
            <button
              className="btn btn-sm btn-light flex items-center gap-2 h-8 sm:h-10"
              onClick={resetFilters}
              aria-label="Clear all filters"
              title="Clear all filters"
            >
              <KeenIcon
                type="solid"
                icon="arrows-circle"
                className="text-gray-600"
              />
              Clear Filters
            </button>
          </div>
        )}
      </div>
      <div className="card-body">
        {isLoading || isFetching ? (
          <div className="text-center py-4">Loading users...</div>
        ) : isError ? (
          <div className="text-center py-4 text-red-500">
            Failed to load users. Please try again.
          </div>
        ) : (
          <DataGrid
            columns={columns}
            data={filteredData}
            rowSelect={false}
            loading={isLoading || isFetching}
            pagination={{
              page: pagination.pageIndex,
              size: pagination.pageSize,
              more: true,
              totalItems: totalItems,
              totalPages: totalPages,
              setPagination,
              info: `{from} - {to} of {count}`,
              sizes: [5, 10, 25, 50, 100],
              sizesLabel: 'Show',
              sizesDescription: 'per page',
            }}
            sorting={[{ id: 'users', desc: false }]}
            serverSide={true}
            onFilterReset={resetFilters}
            hasActiveFilters={
              statusFilter !== 'all' ||
              roleFilter.length > 0 ||
              entityFilter !== 'all' ||
              specificEntityId !== 'all' ||
              tempStartDate !== '' ||
              tempEndDate !== '' ||
              searchTerm !== ''
            }
          />
        )}
        <Modal
          open={modalState.isOpen}
          onClose={handleCloseModal}
          className="!flex items-center justify-center"
          aria-labelledby="user-modal-title"
          aria-describedby="user-modal-description"
        >
          <div className="bg-gray-100 p-6 w-full max-w-md mx-4 rounded-lg shadow-lg flex flex-col max-h-[97dvh] overflow-y-auto">
            <h1
              id="user-modal-title"
              className="text-lg font-medium text-gray-900 mb-4"
            >
              {modalState.mode === 'view' ? 'View User' : 'Edit User'}
            </h1>
            <div id="user-modal-description" className="sr-only">
              {modalState.mode === 'view'
                ? 'View user details'
                : 'Form to edit user details with save and cancel options'}
            </div>
            {isUserLoading ? (
              <div className="text-center py-4">Loading user details...</div>
            ) : formData ? (
              <div
                tabIndex={0}
                role="region"
                aria-label={
                  modalState.mode === 'view'
                    ? 'User details'
                    : 'Edit user form'
                }
                className="flex-1 overflow-y-auto max-h-[70vh] custom-scrollbar focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                <div className="flex flex-col gap-4 pr-3">
                  <div ref={roleDropdownRef}>
                    <label
                      id="role-input-label"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Roles
                    </label>
                    <div className="relative">
                      <div
                        role="combobox"
                        id="role-input"
                        aria-labelledby="role-input-label"
                        aria-haspopup="listbox"
                        aria-disabled={isRoleControlDisabled}
                        tabIndex={isRoleControlDisabled ? -1 : 0}
                        onKeyDown={(e) => {
                          if (
                            isRoleControlDisabled ||
                            e.target !== e.currentTarget
                          ) {
                            return;
                          }
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setIsRoleDropdownOpen(!isRoleDropdownOpen);
                          }
                        }}
                        onClick={() => {
                          if (!isRoleControlDisabled) {
                            setIsRoleDropdownOpen(!isRoleDropdownOpen);
                          }
                        }}
                        className={clsx(
                          'w-full h-10 border rounded-md px-3 py-2 text-left text-sm',
                          'border-gray-300 focus:ring-primary focus:border-primary',
                          modalState.mode === 'view' || isUpdating
                            ? 'bg-gray-100 cursor-not-allowed text-gray-500'
                            : 'bg-gray-100 text-gray-700',
                          'flex items-center justify-between',
                          errors.role && 'border-red-500 focus:ring-red-500'
                        )}
                        aria-expanded={isRoleDropdownOpen}
                        aria-controls="role-dropdown"
                        aria-invalid={!!errors.role}
                        aria-describedby={
                          errors.role ? 'role-error' : undefined
                        }
                      >
                        <span className="flex items-center gap-1 overflow-hidden">
                          {formData.role.length > 0 ? (
                            <span className="flex flex-wrap gap-1">
                              {formData.role.slice(0, 2).map((role) => (
                                <span
                                  key={role}
                                  className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary text-white"
                                >
                                  <span className="truncate max-w-[80px]">
                                    {formatRoleLabel(role)}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRoleChange(role);
                                    }}
                                    className="ml-1 flex-shrink-0 focus:outline-none"
                                    disabled={isRoleControlDisabled}
                                    aria-label={`Remove ${formatRoleLabel(role)}`}
                                  >
                                    <KeenIcon
                                      type="filled"
                                      icon="cross"
                                      className="text-white w-3 h-3"
                                    />
                                  </button>
                                </span>
                              ))}
                              {formData.role.length > 2 && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-700">
                                  +{formData.role.length - 2}
                                </span>
                              )}
                            </span>
                          ) : (
                            <span className="text-gray-500 text-sm">
                              Select role(s)
                            </span>
                          )}
                        </span>
                        <KeenIcon
                          type="filled"
                          icon={isRoleDropdownOpen ? 'up' : 'down'}
                          className="text-gray-500 w-4 h-4"
                        />
                      </div>
                      {isRoleDropdownOpen && (
                        <div
                          id="role-dropdown"
                          className="absolute z-10 mt-2 w-full bg-gray-200 shadow-lg rounded-md border border-gray-200 max-h-48 overflow-y-auto"
                        >
                          {roleOptions.map((option, index) => (
                            <label
                              key={option.value}
                              className={clsx(
                                'flex items-center px-4 py-2 hover:bg-gray-100 cursor-pointer',
                                index === 0 && 'rounded-t-md',
                                index === roleOptions.length - 1 &&
                                  'rounded-b-md',
                                option.value === 'marina' &&
                                  formData.role.includes('boater') &&
                                  'opacity-50',
                                option.value === 'boater' &&
                                  formData.role.includes('marina') &&
                                  'opacity-50'
                              )}
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  handleRoleChange(option.value);
                                }
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={formData.role.includes(option.value)}
                                onChange={() => handleRoleChange(option.value)}
                                disabled={
                                  modalState.mode === 'view' ||
                                  isUpdating ||
                                  (option.value === 'marina' &&
                                    formData.role.includes('boater')) ||
                                  (option.value === 'boater' &&
                                    formData.role.includes('marina'))
                                }
                                className={clsx(
                                  'h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded cursor-pointer',
                                  option.value === 'boater'
                                    ? 'rounded-full'
                                    : ''
                                )}
                                aria-label={`Select ${option.label}`}
                              />
                              <span className="ml-2 text-sm text-gray-700 cursor-pointer">
                                {option.label}
                              </span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                    {errors.role && (
                      <p id="role-error" className="text-red-500 text-sm mt-1">
                        {errors.role}
                      </p>
                    )}
                  </div>
                  <div>
                    <label
                      htmlFor="nationality-input"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Nationality
                    </label>
                    <NationalitySelect
                      id="nationality-input"
                      selected={formData.nationality as string}
                      setFieldValue={handleNationalityChange}
                      disabled={modalState.mode === 'view' || isUpdating}
                      hasError={!!errors.nationality}
                      className="w-full"
                    />
                    {errors.nationality && (
                      <p
                        id="nationality-error"
                        className="text-red-500 text-sm mt-1"
                      >
                        {errors.nationality}
                      </p>
                    )}
                  </div>
                  <div>
                    <label
                      htmlFor="userName-input"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Name
                    </label>
                    <input
                      ref={inputRef}
                      id="userName-input"
                      type="text"
                      name="userName"
                      autoComplete="name"
                      value={formData.user.userName}
                      onChange={handleInputChange}
                      onKeyDown={preventLeadingWhitespace}
                      disabled={modalState.mode === 'view' || isUpdating}
                      className={clsx(
                        'input input-bordered w-full h-10 text-sm border-gray-300 focus:ring-primary focus:border-primary',
                        modalState.mode === 'view' || isUpdating
                          ? 'bg-gray-100 cursor-not-allowed'
                          : 'bg-gray-100',
                        errors.userName && 'border-red-500 focus:ring-red-500'
                      )}
                      aria-invalid={!!errors.userName}
                      aria-describedby={
                        errors.userName ? 'userName-error' : undefined
                      }
                      placeholder="Enter name"
                    />
                    {errors.userName && (
                      <p
                        id="userName-error"
                        className="text-red-500 text-sm mt-1"
                      >
                        {errors.userName}
                      </p>
                    )}
                  </div>
                  <div>
                    <label
                      htmlFor="userGmail-input"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Email
                    </label>
                    <input
                      id="userGmail-input"
                      type="email"
                      name="userGmail"
                      autoComplete="email"
                      value={formData.user.userGmail}
                      onChange={handleInputChange}
                      disabled={true}
                      className={clsx(
                        'input input-bordered w-full h-10 text-sm border-gray-300 focus:ring-primary focus:border-primary bg-gray-100 cursor-not-allowed',
                        errors.userGmail && 'border-red-500 focus:ring-red-500'
                      )}
                      aria-invalid={!!errors.userGmail}
                      aria-describedby={
                        errors.userGmail ? 'userGmail-error' : undefined
                      }
                      placeholder="Enter email"
                    />
                    {errors.userGmail && (
                      <p
                        id="userGmail-error"
                        className="text-red-500 text-sm mt-1"
                      >
                        {errors.userGmail}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <div className="w-1/3">
                      <label
                        htmlFor="countryCode-select"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Country Code
                      </label>
                      <CountryCodeSelect
                        id="countryCode-select"
                        className="w-full bg-gray-100 rounded-md"
                        selected={
                          (formData.phoneCountryShortCode ||
                            formData.countryShortCode) as string
                        }
                        setFieldValue={handleCountryCodeChange}
                        disabled={modalState.mode === 'view' || isUpdating}
                        hasError={
                          !!errors.phoneCountryShortCode || !!errors.countryCode
                        }
                      />
                      {(errors.countryCode || errors.phoneCountryShortCode) && (
                        <p
                          id="countryCode-error"
                          className="text-red-500 text-sm mt-1"
                        >
                          {errors.countryCode || errors.phoneCountryShortCode}
                        </p>
                      )}
                    </div>
                    <div className="w-2/3">
                      <label
                        htmlFor="phoneNumber-input"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Phone Number
                      </label>
                      <input
                        id="phoneNumber-input"
                        type="tel"
                        name="phoneNumber"
                        autoComplete="tel"
                        value={formData.phoneNumber}
                        onChange={(e) => {
                          const digitsOnly = e.target.value.replace(
                            /[^0-9]/g,
                            ''
                          );
                          handleInputChange({
                            target: { name: 'phoneNumber', value: digitsOnly },
                          } as React.ChangeEvent<HTMLInputElement>);
                        }}
                        onBlur={() => handleBlur('phoneNumber')}
                        disabled={modalState.mode === 'view' || isUpdating}
                        className={clsx(
                          'input input-bordered w-full h-10 text-sm border-gray-300 focus:ring-primary focus:border-primary',
                          modalState.mode === 'view' || isUpdating
                            ? 'bg-gray-100 cursor-not-allowed'
                            : 'bg-gray-100',
                          errors.phoneNumber &&
                            'border-red-500 focus:ring-red-500'
                        )}
                        aria-invalid={!!errors.phoneNumber}
                        aria-describedby={
                          errors.phoneNumber ? 'phoneNumber-error' : undefined
                        }
                        placeholder="Enter phone number"
                      />
                      {errors.phoneNumber && (
                        <p
                          id="phoneNumber-error"
                          className="text-red-500 text-sm mt-1"
                        >
                          {errors.phoneNumber}
                        </p>
                      )}
                    </div>
                  </div>
                  <div>
                    <label
                      htmlFor="user-country-select"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Country
                    </label>
                    <CountrySelect
                      id="user-country-select"
                      className="w-full"
                      selected={formData.countryShortCode || ''}
                      setFieldValue={handleUserCountryChange}
                      disabled={modalState.mode === 'view' || isUpdating}
                      hasError={!!errors.countryShortCode}
                    />
                    {errors.countryShortCode && (
                      <p
                        id="user-country-error"
                        className="text-red-500 text-sm mt-1"
                      >
                        {errors.countryShortCode}
                      </p>
                    )}
                  </div>
                  <div>
                    <label
                      htmlFor="status-input"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Status
                    </label>
                    <select
                      id="status-input"
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      disabled={modalState.mode === 'view' || isUpdating}
                      className={clsx(
                        'select select-bordered w-full h-10 text-sm border-gray-300 focus:ring-primary focus:border-primary',
                        modalState.mode === 'view' || isUpdating
                          ? 'bg-gray-100 cursor-not-allowed'
                          : 'bg-gray-100',
                        errors.status && 'border-red-500 focus:ring-red-500'
                      )}
                      aria-invalid={!!errors.status}
                      aria-describedby={
                        errors.status ? 'status-error' : undefined
                      }
                    >
                      {statusOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    {errors.status && (
                      <p
                        id="status-error"
                        className="text-red-500 text-sm mt-1"
                      >
                        {errors.status}
                      </p>
                    )}
                  </div>
                  <div>
                    <label
                      htmlFor="createdAt-input"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Created At
                    </label>
                    <input
                      id="createdAt-input"
                      type="text"
                      name="createdAt"
                      value={formData.createdAt}
                      disabled={true}
                      className="input input-bordered w-full h-10 text-sm border-gray-300 focus:ring-primary focus:border-primary bg-gray-100 cursor-not-allowed"
                      aria-label="User creation date"
                    />
                  </div>
                  {formData.role.includes('marina') && formData.harbor && (
                    <div>
                      <label
                        htmlFor="harbor-input"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Harbor Name
                      </label>
                      <input
                        id="harbor-input"
                        type="text"
                        value={formData.harbor.harbor_name}
                        disabled
                        className="input input-bordered w-full h-10 text-sm border-gray-300 focus:ring-primary focus:border-primary bg-gray-100 cursor-not-allowed"
                        aria-label="Harbor name"
                      />
                    </div>
                  )}
                  {formData.role.includes('boater') && (
                    <div>
                      <span className="block text-sm font-medium text-gray-700 mb-1">
                        Boats
                      </span>
                      {formData.boats && formData.boats.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {formData.boats.map((boat) => (
                            <span
                              key={boat.boat_id}
                              className="inline-flex items-center px-2 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                            >
                              {boat.boat_name}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <div>No boats available</div>
                      )}
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Profile Picture (Optional)
                    </label>
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-2">
                        <input
                          ref={fileInputRef}
                          id="profilePicture-input"
                          type="file"
                          name="profilePicture"
                          accept="image/jpeg,image/jpg,image/png"
                          onChange={handleFileChange}
                          className="hidden"
                          disabled={modalState.mode === 'view' || isUpdating}
                          aria-describedby={
                            errors.profilePicture
                              ? 'profilePicture-error'
                              : undefined
                          }
                        />
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="btn btn-sm btn-light flex items-center justify-center gap-2 w-full sm:w-auto"
                          disabled={modalState.mode === 'view' || isUpdating}
                          aria-label="Choose profile picture"
                        >
                          Choose File
                        </button>
                        {(formData.profilePicture || previewUrl) && (
                          <button
                            type="button"
                            onClick={handleRemoveImage}
                            className="btn btn-sm btn-light btn-icon"
                            disabled={modalState.mode === 'view' || isUpdating}
                            aria-label="Remove profile picture"
                          >
                            <KeenIcon
                              type="filled"
                              icon="trash"
                              className="text-red-500"
                            />
                          </button>
                        )}
                      </div>
                      {previewUrl ? (
                        <div className="flex items-center gap-3">
                          <img
                            src={previewUrl}
                            alt="Profile preview"
                            className="w-16 h-16 rounded-full object-cover border border-gray-300"
                          />
                          {formData.profilePicture && (
                            <div className="flex flex-col">
                              <span className="text-sm text-gray-700">
                                {formData.profilePicture.name}
                              </span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center border border-gray-300">
                          <KeenIcon
                            type="filled"
                            icon="user"
                            className="text-gray-500 text-2xl"
                          />
                        </div>
                      )}
                      {errors.profilePicture && (
                        <p
                          id="profilePicture-error"
                          className="text-red-500 text-sm mt-1"
                        >
                          {errors.profilePicture}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                {/* {modalState.mode === 'view' && userDetails && (
                  <div className="mt-4">
                    <LegalAcceptanceCard
                      summary={
                        (userDetails as UserResponse).legal_acceptance_summary
                      }
                    />
                  </div>
                )} */}
              </div>
            ) : (
              <div className="text-center py-4 text-red-500">
                Failed to load user details.
              </div>
            )}
            {modalState.mode === 'edit' && (
              <div className="mt-6 flex justify-end gap-2">
                <button
                  onClick={handleCloseModal}
                  className="btn btn-sm btn-light"
                  aria-label="Cancel"
                  disabled={isUpdating}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="btn btn-sm btn-primary"
                  aria-label="Save changes"
                  disabled={isUserLoading || isUpdating}
                >
                  Save
                </button>
              </div>
            )}
          </div>
        </Modal>
        <ConfirmationDialog
          open={confirmDialogState.isOpen}
          onClose={handleCloseConfirmDialog}
          onConfirm={handleConfirmStatusToggle}
          message={`Are you sure you want to change the user status to ${confirmDialogState.currentStatus === 'Active' ? 'Inactive' : 'Active'}?`}
          cancelButtonText="No"
          confirmButtonText="Yes"
        />
        <ConfirmationDialog
          open={deleteActionModal.isOpen}
          onClose={() =>
            setDeleteActionModal({
              isOpen: false,
              userId: null,
            })
          }
          onConfirm={handleDeleteUser}
          message={`Are you sure you want to delete this user?`}
          cancelButtonText="No"
          confirmButtonText="Yes"
          aria-labelledby="action-confirmation-title"
          aria-describedby="action-confirmation-description"
        />
        <ConfirmationDialog
          open={revokeTokensModal.isOpen}
          onClose={() =>
            setRevokeTokensModal({
              isOpen: false,
              userId: null,
            })
          }
          onConfirm={handleRevokeUserTokens}
          message="Revoke all active sessions for this user? They will need to sign in again on every device."
          cancelButtonText="No"
          confirmButtonText="Yes"
          aria-labelledby="revoke-tokens-confirmation-title"
          aria-describedby="revoke-tokens-confirmation-description"
        />
      </div>
    </div>
  );
};

export { Users };
